import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// ── Inmate-only PWA scoping ──────────────────────────────────────────────────
// Blue Phoenix is an installable app for the INMATE experience ONLY. Staff,
// provider, and admin portals must never be installable or offer a download.
// Installability is gated by route: the manifest <link> and the app-shell
// service worker are present only on inmate-facing surfaces, and the manifest
// link is stripped on every staff route so no browser install prompt appears.
function isInmateSurface(pathname) {
    return (
        pathname === "/" ||
        pathname === "/claim-access" ||
        pathname === "/forgot-access-code" ||
        pathname.startsWith("/inmate")
    );
}

// Officer PWA — the custody workspace (security rounds, meal log, crisis alerts, head
// count, shift hand off) is installable on a duty phone. Same shell-only worker: no PHI
// is ever cached, and rounds keep their own offline queue in localStorage.
function isOfficerSurface(pathname) {
    return pathname === "/institution" || pathname.startsWith("/institution/custody");
}

let swRegistered = false;

export function PwaController() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const facilityId = user?.institution?.id || user?.institution_id || "";

    useEffect(() => {
        const head = document.head;
        let link = head.querySelector('link[rel="manifest"]');

        const officer = isOfficerSurface(pathname);
        if (isInmateSurface(pathname) || officer) {
            // Facility-branded officer manifest (name/icon per jail) when we know the
            // facility; falls back to the generic officer manifest before sign-in.
            const href = officer
                ? (facilityId ? `/api/public/officer-manifest/${facilityId}` : "/manifest-officer.json")
                : "/manifest.json";
            if (!link) {
                link = document.createElement("link");
                link.rel = "manifest";
                head.appendChild(link);
            }
            if (link.getAttribute("href") !== href) link.href = href;
            // App-shell-only service worker (never caches PHI / /api). Register once.
            if (!swRegistered && "serviceWorker" in navigator) {
                swRegistered = true;
                navigator.serviceWorker.register("/service-worker.js").catch(() => {});
            }
        } else if (link) {
            // Staff surface — remove the manifest so the app is not installable here.
            link.remove();
        }
    }, [pathname, facilityId]);

    return null;
}
